import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useState, type ReactNode } from 'react';
import { db } from '../../data/db';
import type { JobRecord, PhotoRef } from '../../data/types';
import { dateTime } from '../../lib/format';
import { useFileUrl, type FileSource } from '../../sync/files';
import { Icon } from '../../ui/Icon';
import { Modal, useEscapeToClose } from '../../ui/Modal';
import { useUI } from '../../ui/UIProvider';
import { useAuth } from '../../auth/AuthProvider';
import { api } from '../../lib/api';
import { documentHtml, downloadRecord, downloadRecordPdf, labelFor, photoDataUrls } from './recordDocument';

export const photoSource = (photo: PhotoRef): FileSource | null => (photo.dataUrl ? { kind: 'data-url', dataUrl: photo.dataUrl } : photo.fileId ? { kind: 'file', fileId: photo.fileId } : null);


/** A photo thumbnail that loads from this device or the server; shows a placeholder offline. */
export function PhotoThumb({ photo, className, label, onOpen }: { photo: PhotoRef; className: string; label: string; onOpen(src: string): void }) {
  const { url, status } = useFileUrl(photoSource(photo));
  return (
    <button type="button" className={className} aria-label={label} disabled={status !== 'ready'} onClick={() => onOpen(url)} title={status === 'unavailable' ? 'Photo available when back online' : undefined}>
      {status === 'ready' ? <img src={url} alt="" /> : <span className="photo-placeholder">{status === 'loading' ? '…' : 'Offline'}</span>}
    </button>
  );
}

/** Opens a photo full size. The photo stays loaded while the viewer is open. */
export function PhotoViewer({ src, name }: { src: string; name: string }) {
  const ui = useUI();
  useEscapeToClose(() => ui.close('photo'));
  return (
    <div className="lightbox" onClick={(event) => { if (event.target === event.currentTarget) ui.close('photo'); }}>
      <figure role="dialog" aria-modal="true" aria-label="Job evidence photo">
        <button className="icon-button" aria-label="Close photo" onClick={() => ui.close('photo')} autoFocus><Icon name="close" size={20} /></button>
        <img src={src} alt={name} />
        {name && <figcaption>{name}</figcaption>}
      </figure>
    </div>
  );
}

function DetailValue({ value }: { value: unknown }): ReactNode {
  if (Array.isArray(value)) {
    if (!value.length) return <span>None recorded</span>;
    if (value.every((item) => typeof item !== 'object')) return <span>{value.join(', ')}</span>;
    return <div className="detail-nested">{value.map((item, index) => <dl key={index}>{Object.entries(item as object).filter(([key]) => key !== 'dataUrl').map(([key, itemValue]) => <FieldPair key={key} name={key} value={itemValue} />)}</dl>)}</div>;
  }
  if (typeof value === 'object' && value) return <div className="detail-nested"><dl>{Object.entries(value).map(([key, itemValue]) => <FieldPair key={key} name={key} value={itemValue} />)}</dl></div>;
  const text = value === true ? 'Confirmed' : value === false ? 'Not confirmed' : String(value || 'Not recorded');
  return <span style={{ whiteSpace: 'pre-wrap' }}>{text}</span>;
}
const FieldPair = ({ name, value }: { name: string; value: unknown }) => <><dt>{labelFor(name)}</dt><dd><DetailValue value={value} /></dd></>;

export function RecordDetailsModal({ recordId }: { recordId: string }) {
  const ui = useUI();
  const { user } = useAuth();
  const record = useLiveQuery(() => db.jobRecords.get(recordId), [recordId]);
  const [audit, setAudit] = useState<Array<{ action: string; createdAt: string; actor: string | null }>>([]);
  useEffect(() => { if (user?.role !== 'admin' || !record) return; api<{ entries: Array<{ action: string; createdAt: string; actor: string | null }> }>(`/api/records/job-records/${encodeURIComponent(record.id)}/audit`).then(({ entries }) => setAudit(entries)).catch(() => setAudit([])); }, [record, user?.role]);
  if (!record) return null;
  const rows = Object.entries(record.data || {}).filter(([key]) => key !== 'photos' && key !== 'job');
  return (
    <Modal id="recordDetailsModal" size="lg" onClose={() => ui.close('recordDetails')} labelledBy="recordDocumentTitle">
      <header className="modal-header">
        <p className="eyebrow">{record.clientName}</p>
        <h2 id="recordDocumentTitle">{record.type}</h2>
        <p className="form-help">{record.jobService || 'Service record'} · {record.technician || 'Technician not recorded'} · {dateTime(record.createdAt)}</p>
      </header>
      <dl className="detail-list">
        {rows.length ? rows.map(([key, value]) => <div key={key}><dt>{labelFor(key)}</dt><dd><DetailValue value={value} /></dd></div>) : <div><dd>No additional data recorded.</dd></div>}
      </dl>
      <section className="form-section"><h3>Compliance retention</h3><p className="form-help">This completed compliance record is append-only and retained for at least five years from {dateTime(record.createdAt)}.</p>{user?.role === 'admin' && <><h4>Audit history</h4>{audit.length ? <ul>{audit.map((entry, index) => <li key={index}>{dateTime(entry.createdAt)} · {entry.action} · {entry.actor || 'System'}</li>)}</ul> : <p className="form-help">No server audit history is available while offline.</p>}</>}</section>
      {record.photos.length > 0 && (
        <section className="detail-photos"><h3>Photo evidence</h3>
          <div className="photo-grid">{record.photos.map((photo, index) => <PhotoThumb key={index} photo={photo} className="photo-thumb photo-thumb-lg" label={`Open photo ${index + 1}`} onOpen={(src) => ui.open('photo', { src, name: photo.name })} />)}</div>
        </section>
      )}
    </Modal>
  );
}

/** Printable document preview, rendered in a sandboxed frame exactly as it downloads. */
export function RecordPreviewModal({ recordId }: { recordId: string }) {
  const ui = useUI();
  const record = useLiveQuery(() => db.jobRecords.get(recordId), [recordId]);
  const [html, setHtml] = useState('');
  useEffect(() => {
    if (!record) return;
    let cancelled = false;
    photoDataUrls(record as JobRecord).then((photos) => { if (!cancelled) setHtml(documentHtml(record, photos, record.photos.length - photos.length)); });
    return () => { cancelled = true; };
  }, [record]);
  if (!record) return null;
  return (
    <Modal id="recordPreviewModal" size="lg" className="document-modal" onClose={() => ui.close('recordPreview')} labelledBy="recordPreviewTitle">
      <header className="modal-header modal-header-actions">
        <div><p className="eyebrow">Document preview</p><h2 id="recordPreviewTitle">{record.type}</h2><p className="form-help">{record.clientName}</p></div>
        <div className="form-actions"><button className="btn btn-secondary" onClick={() => downloadRecordPdf(record)}>Download PDF</button><button className="btn btn-secondary" onClick={() => downloadRecord(record)}>Download Word</button></div>
      </header>
      {html ? <iframe className="document-frame" title={`${record.type} document preview`} sandbox="" srcDoc={html} /> : <p className="empty-state">Preparing document…</p>}
    </Modal>
  );
}
